import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useServices, localized } from '../hooks/useServices';
import { ServiceIcon, servicePath } from './ServiceCard';

const LOGO_BLUE = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/logo_transparent.png`;

const langNames = { de: 'Deutsch', en: 'English', es: 'Español' };
const langFlags = { de: 'DE', en: 'EN', es: 'ES' };

function ServicesMenu({ services }) {
  const { t, language, lp } = useLanguage();
  return (
    <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 transition-[opacity,transform] duration-200 absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[420px]" data-testid="services-dropdown">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-3">
        {services.map(s => (
          <Link key={s.key} to={lp(servicePath(s))} className="flex items-start gap-3 rounded-xl p-3 hover:bg-blue-50 transition-colors" data-testid={`nav-service-${s.key}`}>
            <span className="w-10 h-10 rounded-lg bg-blue-50 text-[#066ce7] flex items-center justify-center shrink-0">
              <ServiceIcon name={s.icon} className="w-5 h-5" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-[#0F172A]">{localized(s, 'title', language)}</span>
              <span className="block text-xs text-slate-500">{localized(s, 'subtitle', language)}</span>
            </span>
          </Link>
        ))}
        <Link to={lp('/leistungen')} className="block text-center text-sm font-semibold text-[#066ce7] rounded-xl p-2.5 mt-1 bg-slate-50 hover:bg-blue-50 transition-colors" data-testid="nav-all-services">
          {t('nav.allServices')}
        </Link>
      </div>
    </div>
  );
}

export default function Navbar() {
  const { t, language, setLanguage, lp, basePath } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const services = useServices();

  const navLinks = [
    { id: 'home', path: '/', label: t('nav.home') },
    { id: 'about', path: '/about', label: t('nav.about') },
    { id: 'services', path: '/leistungen', label: t('nav.services') },
    { id: 'projects', path: '/projects', label: t('nav.projects') },
    { id: 'planner', path: '/planner', label: t('nav.planner') },
    { id: 'blog', path: '/blog', label: t('nav.blog') },
    { id: 'contact', path: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => (path === '/' ? basePath === '/' : basePath === path || basePath.startsWith(`${path}/`));
  const linkClass = (path) => `text-sm font-medium px-3 py-2 rounded-md transition-colors ${isActive(path) ? 'text-[#066ce7] bg-blue-50' : 'text-slate-700 hover:text-[#066ce7] hover:bg-slate-50'}`;

  return (
    <header className="glass-nav sticky top-0 z-50 border-b border-slate-100" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to={lp('/')} className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
            <img src={LOGO_BLUE} alt="ITCS Industrial" width="98" height="48" className="h-10 md:h-12 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav" aria-label="Hauptnavigation">
            {navLinks.map(link => link.id === 'services' ? (
              <div key={link.id} className="relative group">
                <Link to={lp(link.path)} data-testid="nav-link-services" className={`${linkClass(link.path)} inline-flex items-center gap-1`}>
                  {link.label} <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" aria-hidden="true" />
                </Link>
                {services.length > 0 && <ServicesMenu services={services} />}
              </div>
            ) : (
              <Link key={link.id} to={lp(link.path)} data-testid={`nav-link-${link.id}`} className={linkClass(link.path)}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-[#066ce7] transition-colors px-2 py-1.5 rounded-md hover:bg-slate-50" data-testid="language-switcher" aria-label="Sprache wählen">
                  <Globe className="w-4 h-4" />
                  <span>{langFlags[language]}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.entries(langNames).map(([code, name]) => (
                  <DropdownMenuItem key={code} onClick={() => setLanguage(code)} className={language === code ? 'bg-blue-50 text-[#066ce7]' : ''} data-testid={`lang-option-${code}`}>
                    <span className="font-mono text-xs mr-2">{langFlags[code]}</span>
                    {name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to={lp('/contact')} className="hidden md:block">
              <Button className="bg-[#066ce7] hover:bg-[#005bb5] text-white text-sm font-semibold px-5" data-testid="navbar-cta">
                {t('nav.cta')}
              </Button>
            </Link>

            <button className="lg:hidden p-2 text-slate-700" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" aria-label="Menü">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 animate-fadeIn max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navLinks.map(link => (
              <div key={link.id}>
                <Link to={lp(link.path)} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${link.id}`}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive(link.path) ? 'text-[#066ce7] bg-blue-50' : 'text-slate-700 hover:bg-slate-50'}`}>
                  {link.label}
                </Link>
                {link.id === 'services' && services.map(s => (
                  <Link key={s.key} to={lp(servicePath(s))} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-service-${s.key}`}
                    className="block pl-8 pr-4 py-2 text-sm text-slate-600 hover:text-[#066ce7]">
                    {localized(s, 'title', language)}
                  </Link>
                ))}
              </div>
            ))}
            <Link to={lp('/contact')} onClick={() => setMobileOpen(false)}>
              <Button className="w-full mt-3 bg-[#066ce7] hover:bg-[#005bb5] text-white">{t('nav.cta')}</Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
